import { useState } from "react";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { useAdminGuard } from "@/hooks/useAdminGuard";
import { cn } from "@/lib/utils";
import { Loader2, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import "@/components/admin/admin.css";
import { useAuthUser } from "@/App";

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const user = useAuthUser();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAdmin } = useAdminGuard();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem("admin-sidebar-collapsed") === "true";
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("admin-sidebar-collapsed", String(next));
      } catch {}
      return next;
    });
  };

  if (!user || !isAdmin) {
    return (
      <div className="admin-theme min-h-screen bg-[#F5F3EE] flex items-center justify-center">
        <div role="status" className="flex items-center gap-3 text-sm text-[#746F66]"><Loader2 className="h-5 w-5 animate-spin" />Loading administration…</div>
      </div>
    );
  }

  const sidebarWidth = isCollapsed ? "w-[72px]" : "w-[248px]";
  const contentPadding = isCollapsed ? "md:pl-[72px]" : "md:pl-[248px]";

  return (
    <div className="admin-theme min-h-screen bg-[#F5F3EE] font-sans text-[#1A1A1A]">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 hidden md:block transition-all duration-300 ease-in-out",
          sidebarWidth
        )}
      >
        <AdminSidebar isCollapsed={isCollapsed} onToggle={toggleCollapsed} />
      </aside>

      <div
        className={cn(
          contentPadding,
          "flex flex-col min-h-screen transition-all duration-300 ease-in-out"
        )}
      >
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-[#E5E2DC] bg-[#FAF9F6] px-4 md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild><button aria-label="Open admin navigation" className="rounded-lg border border-[#E5E2DC] p-2"><Menu className="h-5 w-5" /></button></SheetTrigger>
            <SheetContent side="left" className="admin-theme w-[280px] max-w-[85vw] p-0">
              <SheetTitle className="sr-only">Admin navigation</SheetTitle>
              <SheetDescription className="sr-only">Navigate administration pages or return to Admoji.</SheetDescription>
              <AdminSidebar isCollapsed={false} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <span className="text-sm font-semibold">Admoji <span className="ml-1 font-normal text-[#746F66]">/ Admin</span></span>
        </header>
        <main className="admin-content flex-1 min-w-0 p-4 sm:p-6 lg:p-9">
          {children}
        </main>
      </div>
    </div>
  );
}
